import type { Category } from '@avrahami/contracts';
import {
  Alert,
  Button,
  Card,
  CheckboxField,
  ConfirmDialog,
  Loading,
  PageHeader,
  SelectField,
  TextField,
  returnToPrevious,
} from '@avrahami/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState, type SyntheticEvent } from 'react';
import { categoriesQuery, keys, tenantApi } from '../api/tenant';
import { FormStatus, ImageUploadField, mergeErrors, NumberField } from '../components/fields';
import { StaleNotice, useVersionedDraft } from '../components/versioned';
import { he } from '../i18n/he';
import { categoryTree, parentOptions } from '../lib/categories';
import { categoryCreateRequest, categoryDraft, categoryUpdateRequest } from '../lib/drafts';
import { errorMessage, NO_ERRORS, type FieldErrors } from '../lib/forms';

export function CategoryNewPage() {
  const categories = useQuery(categoriesQuery());
  if (categories.isPending) return <Loading />;
  if (categories.isError) return <Alert>{errorMessage(categories.error)}</Alert>;
  return <CategoryEditor category={undefined} all={categories.data.items} />;
}

/** The API has no read of one category: the page takes it from the store's category list. */
export function CategoryEditPage() {
  const { categoryId } = useParams({ from: '/app/categories/$categoryId' });
  const categories = useQuery(categoriesQuery());
  if (categories.isPending) return <Loading />;
  if (categories.isError) return <Alert>{errorMessage(categories.error)}</Alert>;
  const category = categories.data.items.find((item) => item.id === categoryId);
  if (category === undefined) {
    return (
      <>
        <BackLink />
        <Alert>{he.categories.missing}</Alert>
      </>
    );
  }
  return <CategoryEditor key={category.id} category={category} all={categories.data.items} />;
}

function BackLink() {
  return (
    <Link
      to="/categories"
      onClick={returnToPrevious}
      className="mb-2 inline-block text-sm text-brand-700 hover:underline"
    >
      {he.categories.back}
    </Link>
  );
}

/** Name, parent, position, shown or hidden, picture; delete (the store's own categories) with a confirmation. */
function CategoryEditor({
  category,
  all,
}: {
  category: Category | undefined;
  all: readonly Category[];
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // A new category has no version yet (0); an existing one keeps the version its draft came from.
  const editor = useVersionedDraft(
    category ?? null,
    (value) => value?.version ?? 0,
    (value) => categoryDraft(value ?? undefined),
  );
  const { draft, set } = editor;
  const [localErrors, setLocalErrors] = useState<FieldErrors>(NO_ERRORS);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: keys.categories() }),
      queryClient.invalidateQueries({ queryKey: keys.products() }),
    ]);

  const save = useMutation({
    mutationFn: (run: () => Promise<Category>) => run(),
    onSuccess: async (saved) => {
      if (category === undefined) {
        await refresh();
        await navigate({ to: '/categories' });
        return;
      }
      editor.reset(saved);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () =>
      category === undefined ? Promise.resolve() : tenantApi.deleteCategory(category.id),
    onSuccess: async () => {
      setConfirmDelete(false);
      // Leave first: the list without this category would show "not found" here.
      await navigate({ to: '/categories' });
      await refresh();
    },
  });

  const reload = () => {
    if (category === undefined) return;
    save.reset();
    setLocalErrors(NO_ERRORS);
    void queryClient
      .query({ ...categoriesQuery(), staleTime: 0 })
      // A failed read shows on the page itself (the categories query's error).
      .then(
        ({ items }) => {
          const fresh = items.find((item) => item.id === category.id);
          if (fresh !== undefined) editor.reset(fresh);
        },
        () => undefined,
      );
  };
  const onSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (category === undefined) {
      const parsed = categoryCreateRequest(draft);
      setLocalErrors(parsed.ok ? NO_ERRORS : parsed.errors);
      if (parsed.ok) save.mutate(() => tenantApi.createCategory(parsed.value));
      return;
    }
    const parsed = categoryUpdateRequest(draft, editor.baseVersion);
    setLocalErrors(parsed.ok ? NO_ERRORS : parsed.errors);
    if (parsed.ok) save.mutate(() => tenantApi.updateCategory(category.id, parsed.value));
  };
  const errors = mergeErrors(localErrors, save.error);

  return (
    <>
      <BackLink />
      <PageHeader
        title={category === undefined ? he.categories.create : category.name}
        actions={
          category?.origin === 'TENANT_CUSTOM' ? (
            <Button
              tone="danger"
              onClick={() => {
                remove.reset();
                setConfirmDelete(true);
              }}
            >
              {he.common.delete}
            </Button>
          ) : null
        }
      />
      <Card>
        <form
          onSubmit={onSubmit}
          noValidate
          className="flex flex-col gap-4"
          data-testid="category-form"
        >
          <StaleNotice stale={editor.stale} onReload={reload} />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label={he.categories.name}
              name="name"
              required
              maxLength={120}
              value={draft.name}
              error={errors['name']}
              onChange={(event) => {
                set({ name: event.target.value });
              }}
            />
            <SelectField
              label={he.categories.parent}
              name="parentId"
              value={draft.parentId}
              error={errors['parentId']}
              onChange={(event) => {
                set({ parentId: event.target.value });
              }}
            >
              <option value="">{he.categories.topLevel}</option>
              {(category === undefined ? categoryTree(all) : parentOptions(all, category.id)).map(
                ({ category: option, depth }) => (
                  <option key={option.id} value={option.id}>
                    {`${'— '.repeat(depth)}${option.name}`}
                  </option>
                ),
              )}
            </SelectField>
            <NumberField
              label={he.categories.position}
              name="position"
              value={draft.position}
              error={errors['position']}
              onChange={(position) => {
                set({ position });
              }}
            />
            <CheckboxField
              label={he.categories.visible}
              checked={draft.isVisible}
              onChange={(event) => {
                set({ isVisible: event.target.checked });
              }}
            />
          </div>
          <ImageUploadField
            label={he.categories.image}
            hint={he.categories.imageHint}
            value={draft.image}
            error={errors['mediaId']}
            onChange={(image) => {
              set({ image });
            }}
            testId="category-image"
          />
          <FormStatus
            error={save.error}
            localInvalid={Object.keys(localErrors).length > 0}
            saved={save.isSuccess}
            {...(category === undefined ? {} : { onReload: reload })}
          />
          <div>
            <Button type="submit" busy={save.isPending}>
              {category === undefined ? he.common.create : he.common.save}
            </Button>
          </div>
        </form>
      </Card>
      <ConfirmDialog
        open={confirmDelete}
        title={he.common.deleteTitle}
        confirmLabel={he.common.delete}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => {
          remove.mutate();
        }}
        onClose={() => {
          setConfirmDelete(false);
        }}
      >
        <p>{he.common.deleteBody}</p>
        {remove.isError ? (
          <div className="mt-3">
            <Alert>{errorMessage(remove.error, { CONFLICT: he.categories.notEmpty })}</Alert>
          </div>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
